import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { apiClient } from './base/client';
import { ApiError } from './base/errors';

export interface Announcement {
    announcementID: string;
    title: string;
    content: string;
    startDate: string;
    endDate: string;
    active: boolean;
}

export async function fetchAnnouncements(): Promise<Announcement[]> {
    try {
        const endpoint = '/announcements';
        const response = await apiClient.get<Announcement[]>(endpoint);

        return response;
    } catch (error) {
        throw new ApiError('Failed to fetch announcements');
    }
}

export async function fetchActiveAnnouncements(): Promise<Announcement[]> {
    try {
        const endpoint = '/announcements/active';
        const response = await apiClient.get<Announcement[]>(endpoint);

        return response;
    } catch (error) {
        throw new ApiError('Failed to fetch active announcements');
    }
}

export async function createAnnouncement(announcement: Omit<Announcement, 'active'>): Promise<void> {
    try {
        const endpoint = '/announcements';
        await apiClient.post<void>(endpoint, announcement);
    } catch (error) {
        throw new ApiError('Failed to create announcement');
    }
}

export async function updateAnnouncement(
    announcementID: string,
    updates: Partial<Announcement>
): Promise<void> {
    try {
        const endpoint = `/announcements/${announcementID}`;
        await apiClient.patch<void>(endpoint, updates);
    } catch (error) {
        throw new ApiError('Failed to update announcement');
    }
}

export async function deleteAnnouncement(announcementID: string): Promise<void> {
    try {
        const endpoint = `/announcements/${announcementID}`;
        await apiClient.delete<void>(endpoint);
    } catch (error) {
        throw new ApiError('Failed to delete announcement');
    }
}

export const announcementQueryKeys = {
    all: ['announcements'] as const,
    lists: () => [...announcementQueryKeys.all, 'list'] as const,
    list: (filters?: string) => [...announcementQueryKeys.lists(), { filters }] as const,
    active: () => [...announcementQueryKeys.all, 'active'] as const,
    detail: (announcementID: string) => [...announcementQueryKeys.all, 'detail', announcementID] as const,
};

const shouldRetryAnnouncementQuery = (failureCount: number, error: unknown) => {
    if (error instanceof ApiError && error.status === 401) {
        return false;
    }

    if (error instanceof ApiError && error.status && error.status >= 400 && error.status < 500) {
        return false;
    }

    return failureCount < 3;
};

const ANNOUNCEMENTS_STALE_TIME = 60 * 1000;
const ANNOUNCEMENTS_GC_TIME = 5 * 60 * 1000;

export const announcementsQueryOptions = () =>
    queryOptions({
        queryKey: announcementQueryKeys.lists(),
        queryFn: fetchAnnouncements,
        staleTime: ANNOUNCEMENTS_STALE_TIME,
        gcTime: ANNOUNCEMENTS_GC_TIME,
        retry: shouldRetryAnnouncementQuery,
        retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });

export const activeAnnouncementsQueryOptions = () =>
    queryOptions({
        queryKey: announcementQueryKeys.active(),
        queryFn: fetchActiveAnnouncements,
        staleTime: 30 * 1000,
        gcTime: ANNOUNCEMENTS_GC_TIME,
        retry: shouldRetryAnnouncementQuery,
        retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });

export const useAnnouncementsQuery = () => useQuery(announcementsQueryOptions());

export const useActiveAnnouncementsQuery = () => useQuery(activeAnnouncementsQueryOptions());

type UpdateAnnouncementInput = {
    announcementID: string;
    updates: Partial<Announcement>;
};

const invalidateAnnouncementQueries = (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey: announcementQueryKeys.lists() }).catch(() => {});
    queryClient.invalidateQueries({ queryKey: announcementQueryKeys.active() }).catch(() => {});
};

export const useCreateAnnouncement = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (announcement: Omit<Announcement, 'active'>) => createAnnouncement(announcement),
        onSuccess: () => {
            invalidateAnnouncementQueries(queryClient);
        },
    });
};

export const useUpdateAnnouncement = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ announcementID, updates }: UpdateAnnouncementInput) =>
            updateAnnouncement(announcementID, updates),
        onSuccess: () => {
            invalidateAnnouncementQueries(queryClient);
        },
    });
};

export const useDeleteAnnouncement = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (announcementID: string) => deleteAnnouncement(announcementID),
        onSuccess: () => {
            invalidateAnnouncementQueries(queryClient);
        },
    });
};
